import type { AppConfig } from '@/types/config'

/** Build-time API base (e.g. https://xxx.execute-api.region.amazonaws.com/test). Empty = use runtime config from /api/config. */
const API_BASE = import.meta.env.VITE_API_URL ?? ''

let cached: AppConfig | null = null

function configEndpoint(): string {
  if (API_BASE.startsWith('http')) return `${API_BASE.replace(/\/$/, '')}/config`
  const base = window.location.origin + (import.meta.env.BASE_URL ?? '').replace(/\/$/, '')
  return `${base}/api/config`
}

export async function fetchConfig(): Promise<AppConfig> {
  if (cached) return cached
  try {
    const url = configEndpoint()
    const res = await fetch(url)
    if (!res.ok) throw new Error(`Config fetch failed: ${res.status}`)
    const data = await res.json()
    cached = {
      apiUrl: data.apiUrl ?? null,
      wsUrl: data.wsUrl ?? null,
      auth0: data.auth0?.domain && data.auth0?.clientId
        ? {
            domain: data.auth0.domain,
            clientId: data.auth0.clientId,
            audience: data.auth0.audience || undefined,
          }
        : null,
      signUpEnabled: data.signUpEnabled === true,
      cloudWatchDashboardUrl: data.cloudWatchDashboardUrl ?? null,
      grafanaDashboardUrl: data.grafanaDashboardUrl ?? null,
    }
    return cached
  } catch (e) {
    console.warn('Could not load config from API, using defaults.', e)
    cached = {
      apiUrl: null,
      wsUrl: null,
      auth0: null,
      signUpEnabled: false,
      cloudWatchDashboardUrl: null,
      grafanaDashboardUrl: null,
    }
    return cached
  }
}

export function getConfigForAuth0(): { domain: string; clientId: string; audience?: string } | null {
  if (!cached?.auth0) return null
  return {
    domain: cached.auth0.domain,
    clientId: cached.auth0.clientId,
    audience: cached.auth0.audience,
  }
}

/** Alias for fetchConfig (used by router and components). */
export async function ensureConfig(): Promise<AppConfig> {
  return fetchConfig()
}

/** API base URL for REST calls (from config or env). */
export function getApiBaseUrl(): string {
  if (cached?.apiUrl) return cached.apiUrl.replace(/\/$/, '')
  if (API_BASE.startsWith('http')) return API_BASE.replace(/\/$/, '')
  const base = window.location.origin + (import.meta.env.BASE_URL ?? '').replace(/\/$/, '')
  return `${base}/api`
}

/** WebSocket URL for real-time device status (from config). Null if not configured. */
export function getWsUrl(): string | null {
  const url = cached?.wsUrl
  return url ? url.replace(/\/$/, '') : null
}
